// src/pages/Settings.js
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/authContext';
import { supabase } from '../supabaseClient';

const TABS = [
  { key: 'profile', label: 'Profile' },
  { key: 'preferences', label: 'Preferences' },
  { key: 'weights', label: 'Weights' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'privacy', label: 'Privacy' },
  { key: 'teacher', label: 'Teacher' }, // rendered conditionally
];

const DEFAULTS = {
  profile: { avatar_url: null, bio: '', school: '' },
  notifications: { feedback_email: true, classroom_updates: true, weekly_summary: false },
  preferences: {
    difficulty: 'intermediate',
    avoid_repeats: true,
    cooldown_days: 30,
    default_side: 'random',
    timer_seconds: 420,
    feedback_style: 'detailed',
    quiz_reveal: 'after_submit', // or 'immediate'
  },
  weights: { analysis: 35, impact: 35, examples: 20, clarity: 10 },
  teacher: {
    show_class_averages: false,
    auto_assign_difficulty: 'mixed', // easy | mixed | hard
    rubric_template: null,
    auto_lock_after_deadline: false,
    default_deadline_hour: 23,
  },
  appearance: { theme: 'system', font_scale: 1.0, high_contrast: false },
  privacy: { allow_data_use: false },
};

export default function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [savingKey, setSavingKey] = useState(''); // which section is saving
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    let mounted = true;

    async function load() {
      setLoading(true);
      setError('');
      setOk('');

      // Fetch existing settings
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error(error);
        if (mounted) {
          setError('Failed to load settings.');
          setLoading(false);
        }
        return;
      }

      // If none, create defaults row
      if (!data) {
        const insertPayload = {
          user_id: user.id,
          profile: DEFAULTS.profile,
          notifications: DEFAULTS.notifications,
          preferences: DEFAULTS.preferences,
          weights: DEFAULTS.weights,
          teacher: DEFAULTS.teacher,
          appearance: DEFAULTS.appearance,
          privacy: DEFAULTS.privacy,
        };
        const { data: created, error: insertErr } = await supabase
          .from('user_settings')
          .insert(insertPayload)
          .select()
          .single();

        if (insertErr) {
          console.error(insertErr);
          if (mounted) {
            setError('Failed to initialize settings.');
            setLoading(false);
          }
          return;
        }
        if (mounted) {
          setSettings(created);
          setLoading(false);
        }
        return;
      }

      // Merge with defaults (in case new keys were added later)
      const merged = {
        ...data,
        profile: { ...DEFAULTS.profile, ...(data.profile || {}) },
        notifications: { ...DEFAULTS.notifications, ...(data.notifications || {}) },
        preferences: { ...DEFAULTS.preferences, ...(data.preferences || {}) },
        weights: { ...DEFAULTS.weights, ...(data.weights || {}) },
        teacher: { ...DEFAULTS.teacher, ...(data.teacher || {}) },
        appearance: { ...DEFAULTS.appearance, ...(data.appearance || {}) },
        privacy: { ...DEFAULTS.privacy, ...(data.privacy || {}) },
      };

      if (mounted) {
        setSettings(merged);
        setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const isTeacher = user?.role === 'teacher' || user?.isAdmin;

  async function savePatch(patchKey) {
    if (!settings || !user?.id) return;
    setSavingKey(patchKey);
    setError('');
    setOk('');

    let payload = {};
    switch (patchKey) {
      case 'profile':
        payload = { profile: settings.profile };
        break;
      case 'preferences':
        payload = { preferences: settings.preferences };
        break;
      case 'weights':
        payload = { weights: normalizedWeights(settings.weights) };
        break;
      case 'notifications':
        payload = { notifications: settings.notifications };
        break;
      case 'appearance':
        payload = { appearance: settings.appearance };
        break;
      case 'privacy':
        payload = { privacy: settings.privacy };
        break;
      case 'teacher':
        payload = { teacher: settings.teacher };
        break;
      default:
        break;
    }

    const { data, error } = await supabase
      .from('user_settings')
      .update(payload)
      .eq('user_id', user.id)
      .select()
      .maybeSingle();

    if (error) {
      console.error(error);
      setError('Failed to save changes.');
      setSavingKey('');
      return;
    }

    // Merge and confirm
    setSettings((prev) => ({ ...prev, ...payload, updated_at: data?.updated_at || prev?.updated_at }));
    setOk('Saved successfully.');
    setSavingKey('');
    // Clear OK after a moment
    setTimeout(() => setOk(''), 2000);
  }

  function onChangeJson(path, value) {
    // path e.g. "preferences.difficulty"
    setSettings((prev) => {
      const next = { ...prev };
      const [root, key] = path.split('.');
      next[root] = { ...(prev[root] || {}), [key]: value };
      return next;
    });
  }

  function normalizedWeights(weights) {
    // Ensure sum is 100; if zero, fallback to defaults
    const a = Number(weights.analysis) || 0;
    const i = Number(weights.impact) || 0;
    const e = Number(weights.examples) || 0;
    const c = Number(weights.clarity) || 0;
    const total = a + i + e + c;

    if (total === 100) return { analysis: a, impact: i, examples: e, clarity: c };
    if (total <= 0) return { ...DEFAULTS.weights };

    // Normalize proportionally
    const scale = 100 / total;
    const na = Math.round(a * scale);
    const ni = Math.round(i * scale);
    const ne = Math.round(e * scale);
    let nc = 100 - (na + ni + ne); // force sum to 100
    return { analysis: na, impact: ni, examples: ne, clarity: nc };
    // (Clarity gets the rounding remainder)
  }

  const weightsTotal = useMemo(() => {
    if (!settings) return 0;
    const w = settings.weights || {};
    return ['analysis', 'impact', 'examples', 'clarity']
      .map((k) => Number(w[k]) || 0)
      .reduce((a, b) => a + b, 0);
  }, [settings]);

  if (!user) {
    return <div className="p-6 text-gray-600">You must be logged in to view settings.</div>;
  }

  if (loading || !settings) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="h-6 w-40 bg-gray-100 rounded animate-pulse" />
        <div className="h-10 w-full bg-gray-100 rounded animate-pulse" />
        <div className="h-40 w-full bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-600">Control your account, preferences, and training defaults.</p>
        </div>
        {ok && <span className="text-sm text-green-700">{ok}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t) =>
          t.key === 'teacher' && !isTeacher ? null : (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 text-sm whitespace-nowrap ${
                activeTab === t.key
                  ? 'border-b-2 border-blue-600 text-blue-700 font-medium'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          )
        )}
      </div>

      {/* Panels */}
      <div className="space-y-8">
        {activeTab === 'profile' && (
          <section className="bg-white border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Avatar URL</label>
                <input
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.profile?.avatar_url || ''}
                  onChange={(e) => onChangeJson('profile.avatar_url', e.target.value)}
                  placeholder="https://…/avatar.png"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">School / Team</label>
                <input
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.profile?.school || ''}
                  onChange={(e) => onChangeJson('profile.school', e.target.value)}
                  placeholder="e.g., Waterloo Collegiate Institute"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Bio</label>
              <textarea
                rows={4}
                className="w-full rounded border border-gray-300 px-3 py-2"
                value={settings.profile?.bio || ''}
                onChange={(e) => onChangeJson('profile.bio', e.target.value)}
                placeholder="A short bio (for classroom rosters and profile)…"
              />
            </div>
            <div className="pt-2">
              <button
                onClick={() => savePatch('profile')}
                disabled={savingKey === 'profile'}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {savingKey === 'profile' ? 'Saving…' : 'Save Profile'}
              </button>
            </div>
          </section>
        )}

        {activeTab === 'preferences' && (
          <section className="bg-white border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Motion Difficulty</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.preferences?.difficulty || 'intermediate'}
                  onChange={(e) => onChangeJson('preferences.difficulty', e.target.value)}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div className="flex items-center justify-between border rounded px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">Avoid repeated motions</p>
                  <p className="text-xs text-gray-600">Use cooldown window to reduce repeats.</p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-blue-600"
                  checked={!!settings.preferences?.avoid_repeats}
                  onChange={(e) => onChangeJson('preferences.avoid_repeats', e.target.checked)}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Cooldown (days)</label>
                <input
                  type="number"
                  min={0}
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.preferences?.cooldown_days ?? 30}
                  onChange={(e) =>
                    onChangeJson('preferences.cooldown_days', Math.max(0, Number(e.target.value || 0)))
                  }
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Default Side</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.preferences?.default_side || 'random'}
                  onChange={(e) => onChangeJson('preferences.default_side', e.target.value)}
                >
                  <option value="gov">Government</option>
                  <option value="opp">Opposition</option>
                  <option value="random">Random</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Default Speech Timer (seconds)</label>
                <input
                  type="number"
                  min={60}
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.preferences?.timer_seconds ?? 420}
                  onChange={(e) =>
                    onChangeJson('preferences.timer_seconds', Math.max(60, Number(e.target.value || 420)))
                  }
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Writing Feedback Style</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.preferences?.feedback_style || 'detailed'}
                  onChange={(e) => onChangeJson('preferences.feedback_style', e.target.value)}
                >
                  <option value="brief">Brief</option>
                  <option value="detailed">Detailed</option>
                  <option value="rubric">Rubric-based</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Quiz Answer Reveal</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.preferences?.quiz_reveal || 'after_submit'}
                  onChange={(e) => onChangeJson('preferences.quiz_reveal', e.target.value)}
                >
                  <option value="immediate">Immediately</option>
                  <option value="after_submit">After Submit</option>
                </select>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => savePatch('preferences')}
                disabled={savingKey === 'preferences'}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {savingKey === 'preferences' ? 'Saving…' : 'Save Preferences'}
              </button>
            </div>
          </section>
        )}

        {activeTab === 'weights' && (
          <section className="bg-white border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Debate Skill Weights</h2>
            <p className="text-sm text-gray-600">
              Set how AI should weigh different aspects when scoring. Total must equal 100.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              {['analysis', 'impact', 'examples', 'clarity'].map((k) => (
                <div key={k}>
                  <label className="block text-sm text-gray-600 mb-1 capitalize">{k}</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="w-full rounded border border-gray-300 px-3 py-2"
                    value={settings.weights?.[k] ?? 0}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        weights: { ...prev.weights, [k]: Number(e.target.value || 0) },
                      }))
                    }
                  />
                </div>
              ))}
            </div>

            <div className="text-sm">
              <span className={`font-medium ${weightsTotal === 100 ? 'text-green-700' : 'text-amber-700'}`}>
                Total: {weightsTotal} / 100
              </span>
              {weightsTotal !== 100 && (
                <span className="ml-2 text-gray-500">Will normalize on save.</span>
              )}
            </div>

            <div className="pt-2">
              <button
                onClick={() => savePatch('weights')}
                disabled={savingKey === 'weights'}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {savingKey === 'weights' ? 'Saving…' : 'Save Weights'}
              </button>
            </div>
          </section>
        )}

        {activeTab === 'notifications' && (
          <section className="bg-white border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>

            {[
              {
                key: 'feedback_email',
                title: 'Lesson feedback emails',
                desc: 'Email me when AI or a teacher posts feedback on my writing.',
              },
              {
                key: 'classroom_updates',
                title: 'Classroom updates',
                desc: 'Announcements or new lessons from classes I’m in.',
              },
              {
                key: 'weekly_summary',
                title: 'Weekly progress summary',
                desc: 'A roundup of practice and scores.',
              },
            ].map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between border rounded px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">{row.title}</p>
                  <p className="text-xs text-gray-600">{row.desc}</p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-blue-600"
                  checked={!!settings.notifications?.[row.key]}
                  onChange={(e) => onChangeJson(`notifications.${row.key}`, e.target.checked)}
                />
              </div>
            ))}

            <div className="pt-2">
              <button
                onClick={() => savePatch('notifications')}
                disabled={savingKey === 'notifications'}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {savingKey === 'notifications' ? 'Saving…' : 'Save Notifications'}
              </button>
            </div>
          </section>
        )}

        {activeTab === 'appearance' && (
          <section className="bg-white border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Appearance & Accessibility</h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Theme</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.appearance?.theme || 'system'}
                  onChange={(e) => onChangeJson('appearance.theme', e.target.value)}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Font Scale</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.8"
                  max="1.5"
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.appearance?.font_scale ?? 1.0}
                  onChange={(e) =>
                    onChangeJson('appearance.font_scale', Math.min(1.5, Math.max(0.8, Number(e.target.value || 1))))
                  }
                />
              </div>

              <div className="flex items-center justify-between border rounded px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">High contrast</p>
                  <p className="text-xs text-gray-600">Better visibility for text and UI elements.</p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-blue-600"
                  checked={!!settings.appearance?.high_contrast}
                  onChange={(e) => onChangeJson('appearance.high_contrast', e.target.checked)}
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => savePatch('appearance')}
                disabled={savingKey === 'appearance'}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {savingKey === 'appearance' ? 'Saving…' : 'Save Appearance'}
              </button>
            </div>
          </section>
        )}

        {activeTab === 'privacy' && (
          <section className="bg-white border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">AI & Data</h2>

            <div className="flex items-center justify-between border rounded px-3 py-2">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Allow anonymized content to improve suggestions
                </p>
                <p className="text-xs text-gray-600">
                  Your arguments and feedback may be used to personalize your experience.
                </p>
              </div>
              <input
                type="checkbox"
                className="h-5 w-5 accent-blue-600"
                checked={!!settings.privacy?.allow_data_use}
                onChange={(e) => onChangeJson('privacy.allow_data_use', e.target.checked)}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => window.alert('Export not implemented yet.')}
                className="bg-gray-100 border px-3 py-2 rounded text-gray-800 hover:bg-gray-50"
              >
                Download my data
              </button>
              <button
                onClick={() => window.alert('Delete data flow not implemented yet.')}
                className="bg-red-50 border border-red-200 px-3 py-2 rounded text-red-700 hover:bg-red-100"
              >
                Delete my training data
              </button>
            </div>

            <div className="pt-2">
              <button
                onClick={() => savePatch('privacy')}
                disabled={savingKey === 'privacy'}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {savingKey === 'privacy' ? 'Saving…' : 'Save Privacy'}
              </button>
            </div>
          </section>
        )}

        {activeTab === 'teacher' && isTeacher && (
          <section className="bg-white border rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Teacher Defaults</h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between border rounded px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">Show class averages to students</p>
                  <p className="text-xs text-gray-600">Let students view average scores per lesson.</p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-blue-600"
                  checked={!!settings.teacher?.show_class_averages}
                  onChange={(e) => onChangeJson('teacher.show_class_averages', e.target.checked)}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Auto-assign difficulty</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.teacher?.auto_assign_difficulty || 'mixed'}
                  onChange={(e) => onChangeJson('teacher.auto_assign_difficulty', e.target.value)}
                >
                  <option value="easy">Easy</option>
                  <option value="mixed">Mixed</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Rubric template (optional)</label>
                <input
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.teacher?.rubric_template || ''}
                  onChange={(e) => onChangeJson('teacher.rubric_template', e.target.value)}
                  placeholder="e.g., 4-band rubric name"
                />
              </div>

              <div className="flex items-center justify-between border rounded px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">Auto-lock after deadline</p>
                  <p className="text-xs text-gray-600">Prevents late submissions by default.</p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-blue-600"
                  checked={!!settings.teacher?.auto_lock_after_deadline}
                  onChange={(e) => onChangeJson('teacher.auto_lock_after_deadline', e.target.checked)}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Default deadline hour (0–23)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  value={settings.teacher?.default_deadline_hour ?? 23}
                  onChange={(e) =>
                    onChangeJson(
                      'teacher.default_deadline_hour',
                      Math.min(23, Math.max(0, Number(e.target.value || 23)))
                    )
                  }
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => savePatch('teacher')}
                disabled={savingKey === 'teacher'}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {savingKey === 'teacher' ? 'Saving…' : 'Save Teacher Defaults'}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
